/**
 * Live scoring of a performance in the singing room.
 *
 * Turns the singer's microphone into pitch frames while they sing (for the on-beat
 * wave) and into a final score when they stop.
 *
 * ── It taps the room's existing stream ───────────────────────────────────────
 * `VoiceChatRoom` has already called getUserMedia for the WebRTC mesh. Asking for a
 * second stream would prompt twice on some browsers and, worse, some phones hand out
 * a mic with different processing the second time — so the score would be measuring
 * a different signal to the one the room actually hears. Pass the existing
 * MediaStream in.
 *
 * ── What that stream has been through ────────────────────────────────────────
 * The room requests echoCancellation, noiseSuppression and autoGainControl, all of
 * which it needs — without AEC a speaker-user echoes into everyone else's audio.
 * But that processing is aggressive, and it is the reason the one real take on the
 * karaoke studio came back at -44 dBFS peak: recorded on speakers, the canceller
 * treated the backing track as echo and removed most of the voice with it.
 *
 * So this class does NOT silently score whatever it is handed. `levelWarning()`
 * reports when the incoming signal is too quiet to score honestly, so the room can
 * tell the singer to put headphones on before they perform rather than handing them
 * a 0 afterwards.
 */
import { TRACK, applyAdaptiveGate } from './pitchTrack.mjs';
import { FrameTracker } from './frameTracker.mjs';
import { LiveMeter, scoreTake } from './scoring.mjs';

// Re-exported so callers have one import site; the implementation lives in its own
// .mjs because this file needs AudioWorklet/MediaStream and cannot be unit-tested.
export { scoreVocalBuffer } from './scoreBuffer.mjs';

const WORKLET_URL = '/gs-recorder-worklet.js';

/** Below this peak the take cannot be scored honestly — see the note above. */
const QUIET_PEAK = 0.02;
/** Peak that counts as a healthy signal; between the two is a soft warning. */
const GOOD_PEAK = 0.08;

export class LiveScorer {
  /**
   * @param contour  reference contour for the song, or null to run meter-less
   * @param stream   the room's existing MediaStream
   * @param context  the room's AudioContext, reused so timestamps share a clock
   */
  constructor({ contour, stream, context }) {
    this.contour = contour || null;
    this.stream = stream;
    this.ctx = context;
    this.meter = contour ? new LiveMeter(contour) : null;

    this.frames = [];
    this.tracker = null;
    this.node = null;
    this.source = null;
    this.sink = null;

    this.startedAt = null;
    this.peak = 0;
    this.running = false;
  }

  /** Seconds of song elapsed. Set by the stage when the backing track starts. */
  get songTime() {
    if (this.startedAt == null) return 0;
    return Math.max(0, this.ctx.currentTime - this.startedAt);
  }

  async start() {
    if (this.running) return;
    if (!this.ctx.audioWorklet) throw new Error('This browser cannot score singing here');
    await this.ctx.audioWorklet.addModule(WORKLET_URL);
    if (this.ctx.state === 'suspended') await this.ctx.resume();

    this.tracker = new FrameTracker(this.ctx.sampleRate);
    this.node = new AudioWorkletNode(this.ctx, 'gs-recorder', { numberOfOutputs: 1 });
    this.node.port.onmessage = (event) => this.#consume(event.data);

    this.source = this.ctx.createMediaStreamSource(this.stream);
    // Zero-gain sink: the worklet needs to be pulled by the graph, but routing the
    // singer's own mic to the speakers would feed the room back into itself.
    this.sink = this.ctx.createGain();
    this.sink.gain.value = 0;
    this.source.connect(this.node).connect(this.sink).connect(this.ctx.destination);

    this.startedAt = this.ctx.currentTime;
    this.node.port.postMessage('start');
    this.running = true;
  }

  /** Called when the backing track actually begins, so frames map onto song time. */
  markMusicStart() {
    this.startedAt = this.ctx.currentTime;
    this.frames = [];
    this.tracker.reset();
  }

  #consume(block) {
    if (!this.running) return;
    for (const f of this.tracker.push(block)) {
      this.frames.push(f);
      if (this.meter) this.meter.push(f);
    }
    this.peak = this.tracker.peak;
  }

  /**
   * @returns {null|{level:'quiet'|'low'|'ok', peak:number, message:string|null}}
   */
  levelWarning() {
    if (this.peak >= GOOD_PEAK) return { level: 'ok', peak: this.peak, message: null };
    if (this.peak >= QUIET_PEAK) {
      return {
        level: 'low',
        peak: this.peak,
        message: 'Your mic is quiet — move closer or use headphones for a fair score.',
      };
    }
    return {
      level: 'quiet',
      peak: this.peak,
      message: 'We can barely hear you. Use headphones — on speakers the echo canceller '
        + 'removes most of your voice, and the take cannot be scored.',
    };
  }

  /** Current wave/meter reading, or null when the song has no reference contour. */
  read() {
    if (!this.meter) return null;
    return { ...this.meter.read(this.songTime), peak: this.peak };
  }

  /**
   * Stop capturing and score the take.
   * Returns the same shape as the solo studio, so the room and `/fs` show one grade.
   */
  finish() {
    this.stop();
    if (!this.contour) return null;

    const gated = applyAdaptiveGate(this.frames);
    const result = scoreTake(this.contour, gated, { startSec: 0 });
    const durationSec = (this.frames.length * TRACK.hop) / TRACK.sampleRate;
    return {
      ...result,
      detail: {
        durationSec: Number(durationSec.toFixed(1)),
        voicedRatio: result.voicedRatio,
        peak: Number(this.peak.toFixed(4)),
      },
    };
  }

  stop() {
    if (!this.running) return;
    this.running = false;
    try { this.node.port.postMessage('stop'); } catch (e) { /* already torn down */ }
    // Disconnect but do NOT stop the stream's tracks — the room still needs the mic
    // for the voice mesh after the singer's turn ends.
    try { this.source.disconnect(); } catch (e) { /* noop */ }
    try { this.node.disconnect(); } catch (e) { /* noop */ }
    try { this.sink.disconnect(); } catch (e) { /* noop */ }
  }
}
